import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Users, Key, DollarSign, TrendingUp, Download, Bell, Send } from 'lucide-react';

interface DashboardStats {
  totalSiteOwners: number;
  totalLicenses: number;
  totalInstallations: number;
  monthlyRevenue: number;
  totalJobs: number;
}

export function PluginCreatorDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalSiteOwners: 0,
    totalLicenses: 0,
    totalInstallations: 0,
    monthlyRevenue: 0,
    totalJobs: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationMode, setNotificationMode] = useState<'test' | 'all' | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [siteOwnersRes, licensesRes, installationsRes, jobsRes] = await Promise.all([
        supabase.from('site_owners').select('id', { count: 'exact', head: true }),
        supabase.from('plugin_licenses').select('id', { count: 'exact', head: true }),
        supabase.from('plugin_installations').select('id', { count: 'exact', head: true }),
        supabase.from('wordpress_jobs').select('id', { count: 'exact', head: true }),
      ]);

      setStats({
        totalSiteOwners: siteOwnersRes.count || 0,
        totalLicenses: licensesRes.count || 0,
        totalInstallations: installationsRes.count || 0,
        monthlyRevenue: 0,
        totalJobs: jobsRes.count || 0,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPlugin = () => {
    const link = document.createElement('a');
    link.href = 'https://ptfvgyrfluuxgnjnlsps.supabase.co/storage/v1/object/public/plugin-releases/resume-doctor-ai-v6.3.0.zip';
    link.download = 'resume-doctor-ai-v6.3.0.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sendUpdateNotification = async (testMode: boolean) => {
    if (!confirm(testMode ? 'Send test update notification to yourself?' : `Send update notification to all ${stats.totalSiteOwners} site owners?`)) {
      return;
    }

    setSendingNotification(true);
    setNotificationMode(testMode ? 'test' : 'all');

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-plugin-update-notification`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            version: '6.4.0',
            testMode,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send notifications');
      }

      const data = await response.json();
      alert(`Success! ${data.message}`);
    } catch (error) {
      console.error('Error sending notifications:', error);
      alert(`Failed to send notifications: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSendingNotification(false);
      setNotificationMode(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Plugin Creator Dashboard</h1>
          <p className="mt-2 text-gray-600">Overview of your Resume Doctor AI plugin business</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Site Owners</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalSiteOwners}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Licenses</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalLicenses}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Key className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Installations</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalInstallations}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <Download className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalJobs}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-4">
              <button
                onClick={downloadPlugin}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Download className="w-5 h-5" />
                <span>Download Latest Plugin (v6.3.0)</span>
              </button>
              <a
                href="/licenses"
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Key className="w-5 h-5" />
                <span>Manage Licenses</span>
              </a>
              <a
                href="/analytics"
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <TrendingUp className="w-5 h-5" />
                <span>View Analytics</span>
              </a>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Update Notifications
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Notify site owners about the new v6.3.0 plugin release
            </p>
            <div className="space-y-3">
              <button
                onClick={() => sendUpdateNotification(true)}
                disabled={sendingNotification}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingNotification && notificationMode === 'test' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Test Email (to yourself)</span>
                  </>
                )}
              </button>
              <button
                onClick={() => sendUpdateNotification(false)}
                disabled={sendingNotification}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingNotification && notificationMode === 'all' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4" />
                    <span>Notify All Site Owners ({stats.totalSiteOwners})</span>
                  </>
                )}
              </button>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-800">
                Emails will be queued and sent automatically. Site owners will receive instructions on how to update their plugin.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Getting Started</h2>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                <div>
                  <p className="font-medium text-gray-900">Download the plugin</p>
                  <p>Get the latest version of Resume Doctor AI plugin</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                <div>
                  <p className="font-medium text-gray-900">Generate licenses</p>
                  <p>Create license keys for your site owners</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</div>
                <div>
                  <p className="font-medium text-gray-900">Monitor usage</p>
                  <p>Track installations and revenue in analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
